import type { InferGetServerSidePropsType, GetServerSidePropsContext } from "next"
import { getStoryblokApi, StoryblokComponent, useStoryblokState, ISbStoriesParams } from "@storyblok/react"
import { createServerSupabaseClient } from '@supabase/auth-helpers-nextjs'
import Head from 'next/head'

import UnAuth from '@/components/UnAuth'

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const supabase = createServerSupabaseClient(context)
  
  const { data: { session } } = await supabase.auth.getSession()

  const practice = context.query.practice

  const insideStoryblok = context.query._storyblok
  
  const resolveRelations = [
    'practice.tag',
    'practice.tags',
    'practice.tone',
    'practice.style',
    'practice.body',
    'practice.additionalTags',
    'practice.video',
    'practice.recommendedItems',
    'movementRow.movements',
  ]
  
  const sbParams: ISbStoriesParams = {
    version: insideStoryblok ? 'draft' : 'published',
    resolve_relations: resolveRelations
  }

  if (!insideStoryblok && (practice === 'filters' || practice === 'all')) {
    context.res.setHeader(
      'Cache-Control',
      'public, s-maxage=600, stale-while-revalidate=600'
    )
  }

  const { data } = await getStoryblokApi().get(`cdn/stories/practice/${practice}`, sbParams)

  const { seoTitle, seoDescription, seoImage } = data.story.content

  return {
    props: {
      seo: {
        title: seoTitle !== '' ? seoTitle : `${data.story.name} | Rise`,
        description: seoDescription !== '' ? seoDescription : 'Rise is an online wellness and fitness platform. A space to enliven the senses, create rituals and move deeper.',
        image: seoImage.filename ? seoImage.filename : '/images/thumbnail.jpg',
      },
      story: data ? data.story : false,
      session: session,
    },
  }
}

export default function Practice({ story: theStory, session, seo }: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const story = useStoryblokState(theStory)

  if (!session) {
    return (
      <UnAuth />
    )
  }

  return (
    <>
      <Head>
        <title>{seo.title}</title>
        <link rel="icon" href="/favicon.ico" />
        <link rel="canonical" href={`https://rise-practice.com/${theStory.full_slug}`} />
        <meta name="description" content={seo.description} />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:image" content={seo.image} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seo.title} />
        <meta name="twitter:description" content={seo.description} />
        <meta name="twitter:image" content={seo.image} />
        <meta name="facebook-domain-verification" content="llnvqn19atpxt0blmzzlvpl6xd08kb" />
      </Head>
      <StoryblokComponent blok={story.content} id={story.id} uuid={story.uuid} slug={story.slug} />
    </>
  )
}
